import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';
import SiteNavbar from '../components/SiteNavbar';
import SiteFooter from '../components/SiteFooter';
import LineIcon from '../components/LineIcon';
import { fetchTradeCareerData } from '../utils/tradePayData';

const NAVY      = '#0D1B2A';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const GRAY_BG   = '#F6F6F6';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

// key refs marketing.trades.tradesSingular.<key> (name/length/desc), resolved via t() at render
// time -- this module-level constant has no hook access, so it can't call t() itself (same
// pattern as tradePayData.js's MEASUREMENT_INFO).
// payTrade: the matching Career Pay Guide trade (exact trade_career_data.trade name). The card's
// "See pay & unions" link only shows when that trade is actually in the loaded pay data.
const TRADES = [
  { key: 'electrician',                   payTrade: 'Electrician' },
  { key: 'plumber',                       payTrade: 'Plumber' },
  { key: 'welder',                        payTrade: 'Welder and Fabricator' },
  { key: 'hvacTechnician',                payTrade: 'HVAC Technician' },
  { key: 'carpenter',                     payTrade: 'Carpenter' },
  { key: 'pipefitter',                    payTrade: 'Pipefitter/Steamfitter' },
  { key: 'ironworker',                    payTrade: 'Ironworker' },
  { key: 'cncMachinist',                  payTrade: 'Machinist/CNC Machinist' },
  { key: 'industrialMaintenanceMechanic', payTrade: 'Industrial Maintenance Mechanic' },
  { key: 'dieselMechanic',                payTrade: 'Diesel Mechanic' },
  { key: 'machineOperator',               payTrade: 'Machine Operator' },
  { key: 'millwright',                    payTrade: 'Millwright' },
  { key: 'sheetMetalWorker',              payTrade: 'Sheet Metal Worker' },
  { key: 'cdlTruckDriver',                payTrade: 'CDL/Truck Driver' },
  { key: 'aircraftMechanic',              payTrade: 'Aircraft Mechanic (A&P)' },
];
// Career Pay Guide deep links: /resources?tab=pay&trade=<name> selects that trade, &quiz=1 opens the quiz.
const payGuideLink = trade => `/resources?${new URLSearchParams({ tab: 'pay', trade }).toString()}`;
const QUIZ_LINK = '/resources?tab=pay&quiz=1';
const linkStyle = {
  display: 'inline-flex', alignItems: 'center', minHeight: '44px', fontSize: '0.875rem', fontWeight: 700,
  fontFamily: FONT_BODY, textDecoration: 'underline', textUnderlineOffset: '3px',
};

export default function TradesPage() {
  const { t } = useTranslation();
  const [wlForm, setWlForm]           = useState({ name: '', email: '', userType: 'worker', trade: '', city: '' });
  const [wlLoading, setWlLoading]     = useState(false);
  const [wlSubmitted, setWlSubmitted] = useState(false);
  const [showOutOfArea, setShowOutOfArea] = useState(false);
  const navigate = useNavigate();
  // Trades that have a Career Pay Guide entry, for the "See pay & unions" links on the guide cards.
  const [payTrades, setPayTrades] = useState(() => new Set());
  useEffect(() => {
    let cancelled = false;
    fetchTradeCareerData().then(rows => { if (!cancelled) setPayTrades(new Set((rows || []).map(r => r.trade))); });
    return () => { cancelled = true; };
  }, []);

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!wlForm.city.trim()) { alert(t('marketing.enterCityAlert')); return; }
    if (wlForm.userType === 'worker' && !wlForm.trade) { alert(t('marketing.selectTradeAlert')); return; }
    setWlLoading(true);
    try {
      const { error } = await supabase.from('waitlist').insert({
        full_name: wlForm.name,
        email:     wlForm.email,
        user_type: wlForm.userType,
        trade:     wlForm.trade || null,
        city:      wlForm.city || null,
      });
      if (error) console.log('[waitlist] error:', JSON.stringify(error));
      fetch('/api/notify-interest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'waitlist_confirmation', recipient_email: wlForm.email, recipient_name: wlForm.name }),
      }).catch(() => {});
      setWlSubmitted(true);
    } catch (err) {
      console.error('Waitlist signup error:', err);
    } finally {
      setWlLoading(false);
    }
  };

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);

    const style = document.createElement('style');
    style.textContent = `
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
      html { scroll-behavior: smooth; }
      body { background-color: ${NAVY}; }
      @media (max-width: 768px) {
        .tp-nav-links    { display: none !important; }
        .tp-who-grid     { flex-direction: column !important; }
        .tp-steps-row    { flex-direction: column !important; }
        .tp-trades-grid  { grid-template-columns: 1fr !important; }
        .tp-stats-bar    { flex-direction: column !important; gap: 0 !important; }
        .tp-stat-divider { display: none !important; }
        .tp-stat-item    { padding: 1.5rem 1rem !important; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(link);
      document.head.removeChild(style);
    };
  }, []);

  const inp = {
    width: '100%', padding: '12px 14px', fontSize: '0.95rem',
    border: '1.5px solid rgba(13,27,42,0.18)', borderRadius: '8px',
    color: NAVY, outline: 'none', backgroundColor: WHITE, fontFamily: FONT_BODY,
  };
  const lbl = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: NAVY, marginBottom: '6px' };
  const fld = { marginBottom: '1rem' };

  return (
    <div style={{ fontFamily: FONT_BODY, color: WHITE }}>

      {/* ── 1. NAVBAR ── */}
      <SiteNavbar activePage="trades" primaryCta={{ label: t('marketing.joinWaitlist'), onClick: () => navigate('/worker/signup') }} />

      {/* ── 2. HERO ── */}
      <section style={{ backgroundColor: NAVY, padding: '120px 2rem 80px', textAlign: 'center' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h1 style={{ fontFamily: FONT_HEAD, fontSize: 'clamp(3rem, 8vw, 6rem)', letterSpacing: '0.04em', lineHeight: 1.0, marginBottom: '1.5rem' }}>
            <span style={{ color: WHITE, display: 'block' }}>{t('marketing.trades.heroTitle1')}</span>
            <span style={{ color: AMBER, display: 'block' }}>{t('marketing.trades.heroTitle2')}</span>
          </h1>
          <p style={{ fontSize: 'clamp(1rem, 2.5vw, 1.15rem)', color: 'rgba(255,255,255,0.72)', maxWidth: '600px', margin: '0 auto 2.5rem', lineHeight: 1.7 }}>
            {t('marketing.trades.heroSubtitle')}
          </p>
          <button
            onClick={() => navigate('/worker/signup')}
            style={{
              backgroundColor: AMBER, color: NAVY, border: 'none',
              borderRadius: '8px', padding: '14px 36px',
              fontWeight: 700, fontSize: '1.05rem', cursor: 'pointer', fontFamily: FONT_BODY,
            }}
          >
            {t('marketing.trades.joinWaitlistHero')}
          </button>
        </div>
      </section>

      {/* ── 2B. STATS BAR ── */}
      <section style={{ backgroundColor: WHITE, borderTop: `1.5px solid ${AMBER}`, borderBottom: `1.5px solid ${AMBER}` }}>
        <div
          className="tp-stats-bar"
          style={{ maxWidth: '1100px', margin: '0 auto', display: 'flex', alignItems: 'stretch' }}
        >
          {[
            { number: '88,000+', label: t('marketing.stats.newUnionApprentices2025') },
            { number: '2.1M+', label: t('marketing.stats.tradeJobsUnfilled') },
            { number: '25%',   label: t('marketing.stats.vacantByPct') },
          ].map((stat, i) => (
            <React.Fragment key={stat.number}>
              <div
                className="tp-stat-item"
                style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem 1rem', textAlign: 'center' }}
              >
                <div style={{ fontFamily: FONT_HEAD, fontSize: '36px', color: NAVY, lineHeight: 1.1, marginBottom: '6px' }}>{stat.number}</div>
                <div style={{ fontFamily: FONT_BODY, fontSize: '11px', color: '#666666', lineHeight: 1.45, maxWidth: '160px' }}>{stat.label}</div>
              </div>
              {i < 2 && (
                <div
                  className="tp-stat-divider"
                  style={{ width: '1.5px', backgroundColor: AMBER, alignSelf: 'stretch', margin: '1rem 0' }}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* ── 3. WHO THIS IS FOR ── */}
      <section style={{ backgroundColor: '#FFF8EC', padding: '5rem 2rem' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <h2 style={{ fontFamily: FONT_HEAD, fontSize: 'clamp(2rem, 4vw, 2.8rem)', color: NAVY, textAlign: 'center', letterSpacing: '0.05em', marginBottom: '3rem' }}>
            {t('marketing.trades.whoTitle')}
          </h2>
          <div className="tp-who-grid" style={{ display: 'flex', gap: '1.5rem' }}>
            {[
              { icon: 'wrench',     title: t('marketing.trades.who1Title'), body: t('marketing.trades.who1Body') },
              { icon: 'graduation', title: t('marketing.trades.who2Title'), body: t('marketing.trades.who2Body') },
              { icon: 'briefcase',  title: t('marketing.trades.who3Title'), body: t('marketing.trades.who3Body') },
            ].map(card => (
              <div key={card.title} style={{
                flex: 1, borderTop: `4px solid ${AMBER}`, borderRadius: '12px',
                backgroundColor: WHITE, boxShadow: '0 2px 16px rgba(13,27,42,0.07)',
                padding: '2rem 1.75rem',
              }}>
                <div style={{ marginBottom: '1rem' }}><LineIcon name={card.icon} size={34} /></div>
                <h3 style={{ fontWeight: 700, fontSize: '1.05rem', color: NAVY, marginBottom: '0.75rem', fontFamily: FONT_BODY }}>
                  {card.title}
                </h3>
                <p style={{ fontSize: '0.9rem', color: 'rgba(13,27,42,0.58)', lineHeight: 1.65 }}>
                  {card.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4A. HOW IT WORKS — Already in the trades ── */}
      <section style={{ backgroundColor: GRAY_BG, padding: '5rem 2rem' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <h2 style={{ fontFamily: FONT_HEAD, fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', color: NAVY, textAlign: 'center', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>
            {t('marketing.trades.alreadyTitle')}
          </h2>
          <p style={{ textAlign: 'center', fontSize: '0.9rem', color: 'rgba(13,27,42,0.5)', marginBottom: '3rem' }}>
            {t('marketing.trades.alreadySubtitle')}
          </p>
          <div className="tp-steps-row" style={{ display: 'flex', gap: '2rem' }}>
            {[
              { n: '1', title: t('marketing.trades.alreadyStep1Title'), desc: t('marketing.trades.alreadyStep1Desc') },
              { n: '2', title: t('marketing.trades.alreadyStep2Title'), desc: t('marketing.trades.alreadyStep2Desc') },
              { n: '3', title: t('marketing.trades.getHiredTitle'),     desc: t('marketing.trades.alreadyStep3Desc') },
            ].map(step => (
              <div key={step.n} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%', backgroundColor: AMBER,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: FONT_HEAD, fontSize: '1.3rem', color: NAVY, flexShrink: 0, marginBottom: '1rem',
                }}>
                  {step.n}
                </div>
                <h3 style={{ fontWeight: 700, fontSize: '1rem', color: NAVY, marginBottom: '0.5rem', fontFamily: FONT_BODY }}>{step.title}</h3>
                <p style={{ fontSize: '0.88rem', color: 'rgba(13,27,42,0.58)', lineHeight: 1.6 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4B. HOW IT WORKS — New to the trades ── */}
      <section style={{ backgroundColor: NAVY, padding: '5rem 2rem' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <h2 style={{ fontFamily: FONT_HEAD, fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', color: WHITE, textAlign: 'center', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>
            {t('marketing.trades.newTitle')}
          </h2>
          <p style={{ textAlign: 'center', fontSize: '0.9rem', color: 'rgba(255,255,255,0.45)', marginBottom: '3rem' }}>
            {t('marketing.trades.newSubtitle')}
          </p>
          <div className="tp-steps-row" style={{ display: 'flex', gap: '2rem' }}>
            {[
              // There is no school finder on this page -- step 1 links to the Schools tab of Browse Resources.
              { n: '1', title: t('marketing.trades.newStep1Title'), desc: t('marketing.trades.newStep1Desc'), link: { to: '/resources?tab=school', label: t('marketing.trades.newStep1Link') } },
              { n: '2', title: t('marketing.trades.newStep2Title'), desc: t('marketing.trades.newStep2Desc') },
              { n: '3', title: t('marketing.trades.getHiredTitle'), desc: t('marketing.trades.newStep3Desc') },
            ].map(step => (
              <div key={step.n} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%', backgroundColor: AMBER,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: FONT_HEAD, fontSize: '1.3rem', color: NAVY, flexShrink: 0, marginBottom: '1rem',
                }}>
                  {step.n}
                </div>
                <h3 style={{ fontWeight: 700, fontSize: '1rem', color: WHITE, marginBottom: '0.5rem', fontFamily: FONT_BODY }}>{step.title}</h3>
                <p style={{ fontSize: '0.88rem', color: '#CCCCCC', lineHeight: 1.6 }}>{step.desc}</p>
                {step.link && (
                  <Link to={step.link.to} data-schools-link style={{ ...linkStyle, color: AMBER, marginTop: '4px' }}>{step.link.label}</Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. APPRENTICESHIP GUIDE ── */}
      <section style={{ backgroundColor: NAVY, padding: '5rem 2rem' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          {/* Slim strip: opens the Career Pay Guide with the trade quiz already open. */}
          <Link to={QUIZ_LINK} data-quiz-strip style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', flexWrap: 'wrap', textAlign: 'center',
            minHeight: '48px', padding: '10px 18px', marginBottom: '3rem', borderRadius: '10px', textDecoration: 'none',
            backgroundColor: 'rgba(245,166,35,0.1)', border: '1px solid rgba(245,166,35,0.4)',
            color: WHITE, fontSize: '0.95rem', fontWeight: 600, lineHeight: 1.4, fontFamily: FONT_BODY,
          }}>
            <span>{t('marketing.trades.quizStripText')}</span>
            <span style={{ color: AMBER, fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: '3px' }}>{t('marketing.trades.quizStripLink')}</span>
          </Link>
          <h2 style={{ fontFamily: FONT_HEAD, fontSize: 'clamp(2rem, 4vw, 2.8rem)', color: AMBER, textAlign: 'center', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>
            {t('marketing.trades.apprenticeshipGuideTitle')}
          </h2>
          <p style={{ textAlign: 'center', fontSize: '0.95rem', color: 'rgba(255,255,255,0.62)', maxWidth: '680px', margin: '0 auto 3rem', lineHeight: 1.7 }}>
            {t('marketing.trades.apprenticeshipGuideSubtitle')}
          </p>
          <div className="tp-trades-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.25rem' }}>
            {TRADES.map(trade => (
              <div key={trade.key} style={{
                backgroundColor: WHITE, borderTop: `4px solid ${AMBER}`,
                borderRadius: '10px', padding: '1.25rem 1.25rem 1.5rem',
              }}>
                <div style={{ fontWeight: 700, fontSize: '1rem', color: NAVY, marginBottom: '4px', fontFamily: FONT_BODY }}>{t(`marketing.trades.tradesSingular.${trade.key}.name`)}</div>
                <div style={{ fontWeight: 700, fontSize: '0.8rem', color: AMBER, marginBottom: '10px' }}>{t(`marketing.trades.tradesSingular.${trade.key}.length`)}</div>
                <p style={{ fontSize: '0.83rem', color: 'rgba(13,27,42,0.58)', lineHeight: 1.6 }}>{t(`marketing.trades.tradesSingular.${trade.key}.desc`)}</p>
                {payTrades.has(trade.payTrade) && (
                  <Link to={payGuideLink(trade.payTrade)} data-pay-link={trade.payTrade} style={{ ...linkStyle, color: NAVY, marginTop: '6px' }}>
                    {t('marketing.trades.seePayUnions')}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 6. CTA — WAITLIST FORM ── */}
      <section id="cta" style={{ backgroundColor: AMBER, padding: '5rem 2rem' }}>
        <div style={{ maxWidth: '520px', margin: '0 auto' }}>
          <h2 style={{ fontFamily: FONT_HEAD, fontSize: 'clamp(2.2rem, 5vw, 3.2rem)', color: NAVY, textAlign: 'center', letterSpacing: '0.04em', marginBottom: '0.75rem' }}>
            {t('marketing.trades.readyTitle')}
          </h2>
          <p style={{ textAlign: 'center', fontSize: '0.95rem', color: 'rgba(13,27,42,0.68)', marginBottom: '2rem', lineHeight: 1.65 }}>
            {t('marketing.trades.readySubtitle')}
          </p>

          {/* Same split as the landing page: signing up is the real action, and the waitlist
              form is only for people we don't serve yet. Without this the subtitle above would
              promise a live profile and then hand them a waitlist. */}
          {!wlSubmitted && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.25rem', marginBottom: showOutOfArea ? '2.5rem' : 0 }}>
              <button
                data-cta-primary
                onClick={() => navigate('/worker/signup')}
                style={{
                  backgroundColor: NAVY, color: WHITE, border: 'none',
                  borderRadius: '8px', padding: '15px 38px', maxWidth: '100%',
                  fontWeight: 700, fontSize: '1.02rem', cursor: 'pointer', fontFamily: FONT_BODY,
                }}
              >
                {t('marketing.joinWaitlist')}
              </button>
              {!showOutOfArea && (
                <button
                  data-out-of-area-toggle
                  onClick={() => setShowOutOfArea(true)}
                  style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    fontFamily: FONT_BODY, fontSize: '0.9rem', fontWeight: 600,
                    color: 'rgba(13,27,42,0.7)', textDecoration: 'underline',
                    textUnderlineOffset: '3px', maxWidth: '100%',
                  }}
                >
                  {t('marketing.outOfAreaLink')}
                </button>
              )}
            </div>
          )}

          {showOutOfArea && !wlSubmitted && (
            <>
              <h3 style={{
                fontFamily: FONT_HEAD, fontSize: 'clamp(1.6rem, 4.5vw, 2.2rem)', color: NAVY,
                letterSpacing: '0.04em', textAlign: 'center', marginBottom: '0.6rem',
              }}>
                {t('marketing.landing.outOfAreaTitle')}
              </h3>
              <p style={{ textAlign: 'center', fontSize: '0.92rem', color: 'rgba(13,27,42,0.68)', marginBottom: '2rem', lineHeight: 1.65 }}>
                {t('marketing.landing.outOfAreaSubtitle')}
              </p>
            </>
          )}

          {wlSubmitted ? (
            <div style={{
              backgroundColor: WHITE, borderRadius: '12px', padding: '2.5rem 2rem',
              textAlign: 'center', color: NAVY, fontWeight: 600, fontSize: '1.05rem', lineHeight: 1.7,
            }}>
              {t('marketing.trades.onListConfirm')}<br />
              {t('marketing.trades.expansionNote')}
            </div>
          ) : !showOutOfArea ? null : (
            <form onSubmit={handleWaitlist} style={{ backgroundColor: WHITE, borderRadius: '12px', padding: '2rem 2rem 2.25rem' }}>
              <div style={fld}>
                <label style={lbl}>{t('marketing.trades.fullNameLabel')}</label>
                <input required style={inp} placeholder="Jose Rivera" value={wlForm.name} onChange={e => setWlForm(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div style={fld}>
                <label style={lbl}>{t('marketing.trades.emailLabel')}</label>
                <input required type="email" style={inp} placeholder="jose@example.com" value={wlForm.email} onChange={e => setWlForm(p => ({ ...p, email: e.target.value }))} />
              </div>
              <div style={fld}>
                <label style={lbl}>{t('marketing.trades.iAmALabel')}</label>
                <select required style={{ ...inp, cursor: 'pointer' }} value={wlForm.userType} onChange={e => setWlForm(p => ({ ...p, userType: e.target.value }))}>
                  <option value="worker">{t('marketing.trades.workerLookingOption')}</option>
                  <option value="new_to_trades">{t('marketing.trades.newToTradesOption')}</option>
                </select>
              </div>
              <div style={fld}>
                <label style={lbl}>{t('marketing.trades.whatIsYourTradeLabel')}{wlForm.userType === 'worker' ? '' : ` ${t('marketing.trades.optional')}`}</label>
                <select
                  required={wlForm.userType === 'worker'}
                  style={{ ...inp, cursor: 'pointer' }}
                  value={wlForm.trade}
                  onChange={e => setWlForm(p => ({ ...p, trade: e.target.value }))}
                >
                  <option value="">{t('marketing.tradeOptions.selectYourTrade')}</option>
                  <option value="Electrician">{t('marketing.tradeOptions.electrician')}</option>
                  <option value="HVAC Technician">{t('marketing.tradeOptions.hvacTechnician')}</option>
                  <option value="Welder">{t('marketing.tradeOptions.welder')}</option>
                  <option value="Plumber">{t('marketing.tradeOptions.plumber')}</option>
                  <option value="Pipefitter">{t('marketing.tradeOptions.pipefitter')}</option>
                  <option value="Machinist / CNC Operator">{t('marketing.tradeOptions.machinistCncOperator')}</option>
                  <option value="Carpenter">{t('marketing.tradeOptions.carpenter')}</option>
                  <option value="Automotive Tech">{t('marketing.tradeOptions.automotiveTech')}</option>
                  <option value="Diesel Mechanic">{t('marketing.tradeOptions.dieselMechanic')}</option>
                  <option value="Industrial Maintenance">{t('marketing.tradeOptions.industrialMaintenance')}</option>
                  <option value="Machine Operator">{t('marketing.tradeOptions.machineOperator')}</option>
                  <option value="General Maintenance">{t('marketing.tradeOptions.generalMaintenance')}</option>
                  <option value="Looking to choose a trade">{t('marketing.tradeOptions.lookingToChoose')}</option>
                  <option value="Other">{t('marketing.tradeOptions.other')}</option>
                </select>
              </div>
              <div style={fld}>
                <label style={lbl}>{t('marketing.trades.cityLabel')}</label>
                <input required style={inp} placeholder="Milwaukee" value={wlForm.city} onChange={e => setWlForm(p => ({ ...p, city: e.target.value }))} />
              </div>
              <button
                type="submit"
                disabled={wlLoading}
                style={{
                  width: '100%', padding: '13px', backgroundColor: NAVY, color: WHITE,
                  border: 'none', borderRadius: '8px', fontWeight: 700,
                  fontSize: '1rem', cursor: wlLoading ? 'default' : 'pointer', fontFamily: FONT_BODY,
                  opacity: wlLoading ? 0.7 : 1, marginTop: '0.25rem',
                }}
              >
                {wlLoading ? t('marketing.trades.submitting') : t('marketing.trades.joinTheWaitlistBtn')}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ── 8. FOOTER ── */}
      <SiteFooter />

    </div>
  );
}
